import Vue from 'vue';
import {IApi} from '@/models/interfaces/IApi';
import {AxiosInstance, AxiosRequestConfig} from 'axios';
import {FpAxios} from '@/services/FpAxios';
import {Prospect, ProspectDto, ProspectEkoInput, ProspectMeetingDto, ProspectNoteDto} from '@/models/Prospect';
import {FiltersResponse, ProspectFilter} from '@/models/Filter';
import {ProspectStatus} from '@/modules/prospects/enums/ProspectStatus';
import Deal, {DealDTO} from '@/models/Deal';
import Client, {ClientDTO} from '@/models/Client';
import DealsApi from '@/modules/deals/services/DealsApi';
import ClientApi from '@/modules/client/services/ClientApi';
import ActivityApi from '@/modules/activities/services/ActivityApi';
import {IMeeting} from '@/models/interfaces/IMeeting';
import {AddressMeetingKind} from '@/commons/enums/AddressMeetingKind';
import {UserListElement, UserListElementDto} from '@/models/ppl/UserListElement';
import {HistoryElement, HistoryElementDTO} from '@/models/HistoryElement';
import Note, {NoteDTO} from '@/models/Note';
import {ProspectLossReasonDictionaryType} from '@/types/Dictionaries';
import {LossReason} from '@/types/ProspectLossReason';
import {TransferSourceType} from '@/modules/prospects/enums/TransferSourceType';
import {ScenarioStep} from '@/modules/prospects/enums/ScenarioStatus';
import {Utils} from '@/services/utils/BasicUtils';
import AuthService from '@/modules/user/AuthService';
import Activity from '@/models/Activity';
import Logger from '@/services/log/Logger';
import {DealStatus} from '@/modules/deals/enums/DealStatus';
import {chain} from 'lodash-es';
import qs from 'qs';
import {ProductType} from '@/commons/enums/ProductType';
import {AgreementMethod, getAgreementByType} from '@/modules/client/views/client-agreements/Agreements';
import {ProspectSearchClientsInOrganisations} from '@/types/ProspectSearchClientsInOrganisations';
import Partner from '@/models/Partner';
import {UserAdditionalFilters} from '@/models/user/UserAdditionalFilters';
import useGeoAreas from '@/composables/useGeoAreas';
import { UserDTO } from '@/models/user/UserDTO';
import User from '@/models/user/User';
import { DealAttributes } from '@/models/DealAttributes';
import processProxy from '@/env/processProxy';
import EnvironmentService from '@/env/EnvironmentService';
import { mapFromOldModelAgreement, mapToOldModelAgreement, OldAgreements } from '@/modules/client/views/client-agreements/AgreementsUtils';

export type ClientFromPhoneResponse = Nullable<{ client: Client, owner: UserListElement, deals: Array<Deal>, }>;

class ProspectApi implements IApi {
  public readonly fpAxios: AxiosInstance = FpAxios.create();

  readonly baseUrl: string = processProxy.VUE_APP_USER_DEALS_BASE_URL!;

  async getProspects(options: ProspectFilter): Promise<FiltersResponse<Prospect>> {
    try {
      const response = await this.fpAxios.post<FiltersResponse<ProspectDto>>(`${this.baseUrl}prospects`, options.apiData);
      return {
        items: response.data.items.map(dto => new Prospect(dto)),
        quantity: response.data.quantity,
      };
    } catch (e) {
      const msg = (e.response.data && e.response.data.message) ? e.response.data.message : 'Wystąpił błąd';
      Vue.prototype.$snackbarService.openErrorSnackbar(msg);
      throw e;
    }
  }

  async getUsersList(meeting: IMeeting): Promise<UserListElement[]> {
    const config: AxiosRequestConfig = {
      params: {
        land_id: meeting.landId,
        district_id: meeting.districtId,
      },
    };
    const response = await this.fpAxios.get<UserListElementDto[]>('users/location/find', config);
    return response.data.map(person => new UserListElement(person));
  }

  async getUsersForMeeting(prospect: Prospect, additionalFilters?: UserAdditionalFilters): Promise<UserListElement[]> {
    const meeting = prospect.meeting;
    const config: AxiosRequestConfig = {
      params: {
        land_id: meeting.landId,
        district_id: meeting.districtId,
        language: additionalFilters
          ? chain(additionalFilters.languages).pickBy((v) => !!v).keys().value()
          : [],
        product_type: additionalFilters
          ? chain(additionalFilters.products).pickBy((v) => !!v).keys().value()
          : [],
        widget_owner_id: prospect?.source?.widgetOwnerId,
        // TODO prospect
        // this params determin if we want to fetch only desire expert sepcified in this param
        // refactor this after honorata
        user_id: prospect.userId || null,
        with_branch: !!additionalFilters?.with_branch,
        with_address: !!additionalFilters?.with_address,
      },
      paramsSerializer: params => {
        return qs.stringify(params, {arrayFormat: 'repeat',});
      },
    };

    // modyfiyng config params
    const response = await this.fpAxios.get<UserListElementDto[]>('users/location/find-or-get-mobile', config);
    return response.data.map(person => new UserListElement(person));
  }

  // tp for prospect remove refactor this after honorata
  async getUsersForMeeting2(prospect: Prospect, additionalFilters?: UserAdditionalFilters): Promise<User[]> {
    const meeting = prospect.meeting;
    const config: AxiosRequestConfig = {
      params: {
        land_id: meeting.landId,
        district_id: meeting.districtId,
        language: additionalFilters
          ? chain(additionalFilters.languages).pickBy((v) => !!v).keys().value()
          : [],
        product_type: additionalFilters
          ? chain(additionalFilters.products).pickBy((v) => !!v).keys().value()
          : [],
        widget_owner_id: prospect.source?.widgetOwnerId,
        with_branch: !!additionalFilters?.with_branch,
        with_address: !!additionalFilters?.with_address,
      },
      paramsSerializer: params => {
        return qs.stringify(params, {arrayFormat: 'repeat',});
      },
    };

    // modyfiyng config params
    const response = await this.fpAxios.get<UserDTO[]>('users/location/find-or-get-mobile', config);
    return response.data.map(person => new User(person));
  }

  public async getClientDataFromPhoneNumber(phone: string, productType: ProductType): Promise<ClientFromPhoneResponse> {
    try {
      const response = await this.fpAxios.get<{ client: ClientDTO, owner: UserListElementDto, deals: Array<DealDTO>, }[]>(`/clients/prospects/phone/${phone}`,
        {
          params: {
            product_type: productType,
          },
        });
      const pickedClient = response.data.sort((a, b) =>
        new Date(a.client.addedAt) < new Date(b.client.addedAt) ? 1 : -1)[0];
      return pickedClient ? {
        client: new Client(pickedClient.client),
        owner: new UserListElement(pickedClient.owner),
        deals: pickedClient.deals.map((dealDto: DealDTO) => new Deal(dealDto)),
      } : null;
    } catch (e) {
      return null;
    }
  }

  public async saveSurveyAndMeetingData(
    prospectId: number,
    meetingData: ProspectMeetingDto,
    prospectEkoInput: ProspectEkoInput): Promise<void> {
    const geoArea = useGeoAreas();
    if (meetingData.districtId) {
      meetingData.district = geoArea.getDistrictNameById(meetingData.districtId)!;
    }
    if (meetingData.landId) {
      meetingData.voivodship = geoArea.getLandNameById(meetingData.landId)!;
    }
    await this.updateProspect({
      id: prospectId,
      meeting: meetingData,
      ekoInput: prospectEkoInput,
    });
  }

  public async changeStatus(prospectId: number, status: ProspectStatus): Promise<ProspectStatus> {
    await this.updateProspect({
      id: prospectId,
      contact: {status,},
    });
    return status;
  }

  public async changeScenarioStep(prospectId: number, scenarioStep: ScenarioStep): Promise<void> {
    await this.updateProspect({
      id: prospectId,
      scenarioStep: scenarioStep,
    });
  }

  async getProspect(id: string): Promise<{prospect: Prospect, prospectDto: ProspectDto}> {
    const response = await this.fpAxios.get<ProspectDto>(`${this.baseUrl}prospect/${id}`);
    const agreements = mapFromOldModelAgreement(response.data.agreement as OldAgreements || {}, AgreementMethod.PHONE);
    return {prospect: new Prospect(new ProspectDto({...response.data, agreement: agreements})), prospectDto: response.data};
  }


  private prepareClient(prospect: Prospect): Client {
    if (!prospect.client) {
      prospect.createClientFromProspectContact();
    }

    this.trimClientFields(prospect.client!);

    if (prospect.source?.widgetOwnerId) {
      prospect.client!.widgetOwnerId = prospect.source.widgetOwnerId;
    }

    prospect.client!.ownerId = prospect.userId!;
    const processingAgreement = getAgreementByType(prospect.client!.agreements, `Accepts${EnvironmentService.Environment.agreementOrganisation}PersonalDataProcessing`);
    const commercialInformationAgreement = getAgreementByType(prospect.client!.agreements, `Accepts${EnvironmentService.Environment.agreementOrganisation}CommercialInformation`);

    if (processingAgreement && !processingAgreement.method) {
      processingAgreement.method = AgreementMethod.PHONE;
    }

    if (commercialInformationAgreement && !commercialInformationAgreement.method) {
      commercialInformationAgreement.method = AgreementMethod.PHONE;
    }

    return prospect.client!;
  }

  private trimClientFields(client: Client): void {
    client.firstName = this.trimNullable(client.firstName);
    client.lastName = this.trimNullable(client.lastName);
    client.email = this.trimNullable(client.email);
    client.name = this.trimNullable(client.name);
    client.nip = this.trimNullable(client.nip);
  }

  private trimNullable(value: Nullable<string>): Nullable<string> {
    return typeof value === 'string' ? value.trim() : value;
  }

  private prepareDeal(prospect: Prospect): Deal {
    if (prospect.isRealEstate) {
      prospect.deal.realEstateQuestionnaireId = prospect.realEstateQuestionnaire.id!;
    }

    prospect.deal.agentCCId = AuthService.User?.id!;
    prospect.deal.name = `${prospect.dealNamePrefix} ${prospect.contact.displayName}`;
    prospect.deal.productType = prospect.dealType!;
    prospect.deal.agentId = prospect.userId!;
    if (prospect.client!.id) {
      prospect.deal.clientIds = [prospect.client!.id,];
    }
    prospect.deal.loanAmount = prospect.loan?.amount;
    prospect.deal.usercomDealId = prospect.usercomDealId;
    prospect.deal.partnerId = prospect?.source?.finpackPartnerId;
    prospect.deal.prospectUuid = prospect.uuid;
    prospect.deal.status = prospect.activity.isPhoneMeeting
      ? DealStatus.COMMUNICATION_ARRANGED
      : DealStatus.APPOINTMENT_ARRANGED;
    return prospect.deal;
  }

  private prepareActivity(prospect: Prospect, client: Client, deal: Deal): Activity {
    const prefix = prospect.activity.isPhoneMeeting ? 'Rozmowa telefoniczna z' : 'Spotkanie z';
    if (!prospect.activity.addressId && prospect.activity.isMeetingInClientHome && client.mainAddress.id) {
      prospect.activity.addressId = client.mainAddress.id;
    }

    if (!prospect.activity.addressMeetingKind && prospect.activity.addressId) {
      Logger.error(`addressMeetingKind is null, but addressId exist in prospect: ${prospect.id}`);
      prospect.activity.addressId = undefined;
    }

    prospect.activity.name = `${prefix} ${client.displayName}`;
    prospect.activity.dealId = deal.id;
    prospect.activity.clientId = client.id;
    prospect.activity.assignedTo = prospect.userId!;

    if (client && prospect.activity.addressMeetingKind === AddressMeetingKind.CLIENT) {
      prospect.activity.addressId = client.mainAddress.id;
    }

    return prospect.activity;
  }

  private prepareDealAttributes(prospect: Prospect): Partial<DealAttributes> {
    return {
      customerCommitmentLevel: prospect.ekoInput.customerCommitmentLevel,
      customerTimeToBuy: prospect.ekoInput.customerTimeToBuy,
      interest: prospect.ekoInput.customerInterest,
    };
  }

  async passProspect(prospect: Prospect): Promise<Prospect> {
    if (!prospect.userId) {
      const message = 'Ekspert nie został przypisany poprawnie do spotkania';
      Vue.prototype.$snackbarService.openErrorSnackbar(message);
      throw new Error(message);
    }

    this.prepareClient(prospect);
    this.prepareDeal(prospect);
    this.prepareActivity(prospect, prospect.client!, prospect.deal);

    if (!prospect.deal.id) {
      const createResponse = await DealsApi.createDealWithActivity({
        deal: prospect.deal,
        activity: prospect.activity,
        client: prospect.client?.id ? undefined : prospect.client,
        attributes: this.prepareDealAttributes(prospect),
      });

      prospect.deal.agent = createResponse.deal.agent;
      prospect.deal.id = createResponse.deal.id;
      if (createResponse.client?.id) {
        prospect.client = createResponse.client;
      }
      prospect.activity = createResponse.activity;
    } else {
      if (!prospect.client?.id) {
        prospect.client = await ClientApi.addClientByPhone(prospect.client!);
      }

      prospect.activity = await ActivityApi.addActivity(prospect.activity);
      if (prospect.isMortgage && !Utils.isEmpty(prospect.ekoInput)) {
        await this.setDealAttributes(prospect);
      }
    }

    const response = await this.updateProspectAfterPassing(prospect);
    prospect.agent = response.agent;
    prospect.finpackDealId = response.finpackDealId;
    prospect.contact.status = response.contact.status;
    prospect.readOnlyActivity = response.readOnlyActivity;
    return prospect;
  }

  async setDealAttributes(prospect: Prospect) {
    await DealsApi.addAttributes(prospect.deal.id, {
      customerCommitmentLevel: prospect.ekoInput.customerCommitmentLevel,
      customerTimeToBuy: prospect.ekoInput.customerTimeToBuy,
      interest: prospect.ekoInput.customerInterest,
      // note: prospect.activity.description,
    });
  }

  async updateProspectAfterPassing(prospect: Prospect): Promise<Prospect> {
    const data: PartialDeep<ProspectDto> = {
      contact: {
        status: prospect.activity.isPhoneMeeting ? ProspectStatus.ARRANGED_CALL : ProspectStatus.ARRANGED_MEETING,
      },
      id: prospect.id,
      meeting: prospect.meeting,
      agentCC: {
        id: AuthService.User!.id,
      },
      agent: {
        name: `${prospect.deal!.agent!.firstName} ${prospect.deal!.agent!.lastName}`,
        crmId: prospect.deal!.agent!.userId,
        email: prospect.deal!.agent!.email,
      },
      finpackDealId: prospect.deal.id,
      activityAt: prospect.activity.startDate!,
      activity: {
        id: prospect.activity.id,
        addressMeetingKind: prospect.activity.addressMeetingKind!,
        updatedAt: prospect.activity.updatedDate,
        kind: prospect.activity.kind,
        createdAt: prospect.activity.createdDate,
        startedAt: prospect.activity.startDate!,
        isDone: prospect.activity.isDone,
      },
    };
    if (data.meeting && data.agent && data.finpackDealId) {
      return this.updateProspect(data);
    } else {
      throw new Error(`Can't update prospect: ${JSON.stringify(data)}`);
    }
  }

  async addLossReasonToProspect(prospectId: number, lossReason: LossReason): Promise<Prospect> {
    const data: PartialDeep<ProspectDto> = {
      id: prospectId,
      contact: {
        lossReason: lossReason.lossReason,
        lossReasonDescription: lossReason.lossReasonDescription,
        status: ProspectStatus.CLOSED,
      },
    };
    return this.updateProspect(data);
  }

  async updateProspect(prospect: PartialDeep<ProspectDto>): Promise<Prospect> {
    prospect.userId = prospect.agent?.crmId;
    const response = await this.fpAxios.put(`${this.baseUrl}prospect/${prospect.id}`, prospect);
    return response.data;
  }

  async removeAgentFromProspect(prospect: PartialDeep<ProspectDto>): Promise<Prospect> {
    prospect.agent = null;
    return this.updateProspect(prospect);
  }

  async updateProspectByFinpackId(dealId: number, prospect: PartialDeep<Prospect>): Promise<void> {
    await this.fpAxios.put(`${this.baseUrl}prospect/finpack/${dealId}`, prospect);
  }

  async addProspect(prospect: Partial<ProspectDto>, pickedPartner: Partner): Promise<Prospect> {
    prospect.ekoInput = new ProspectEkoInput();
    prospect.ekoInput.customerInterest = prospect.source!.interest;
    const pickedSource = pickedPartner
      .sources
      .find(source => source.id === prospect.source!.finpackSourceId);
    if (pickedSource) {
      prospect.source!.name = pickedSource.name;
    }
    prospect.source!.finpackPartnerId = pickedPartner.id;
    prospect.source!.finpackPartnerName = pickedPartner.name;
    if (prospect.contact) {
      prospect.contact.status = ProspectStatus.CALLED;
    }
    if (prospect.meeting) {
      const geoArea = useGeoAreas();
      prospect.meeting.district = geoArea.getDistrictNameById(prospect.meeting.districtId!)!;
      prospect.meeting.voivodship = geoArea.getLandNameById(prospect.meeting.landId!)!;
    }
    if (prospect.agreement) {
      const processingAgreement = getAgreementByType(prospect.agreement, `Accepts${EnvironmentService.Environment.agreementOrganisation}PersonalDataProcessing`);
      const commercialInformationAgreement = getAgreementByType(prospect.agreement, `Accepts${EnvironmentService.Environment.agreementOrganisation}CommercialInformation`);
      const homeEstateAgreement = getAgreementByType(prospect.agreement, `AcceptsHomeEstatePersonalDataProcessing`);

      if (processingAgreement && !processingAgreement.method) {
        processingAgreement.method = AgreementMethod.PHONE;
      }

      if (commercialInformationAgreement && !commercialInformationAgreement.method) {
        commercialInformationAgreement.method = AgreementMethod.PHONE;
      }

      if (homeEstateAgreement && !homeEstateAgreement.method) {
        homeEstateAgreement.method = AgreementMethod.PHONE;
      }
    }

    prospect.key = '';
    if (prospect.loan) {
      prospect.loan.approval = true;
      prospect.loan.amount = 1;
    }

    if (prospect.source) {
      prospect.source.transfer = TransferSourceType.InBound;
    }

    //TODO: do usunięcia jak na BE zmienią model na nowy w deals-entrypoint
    const prospectToSend = {
      ...prospect,
      agreement: mapToOldModelAgreement(prospect.agreement || []),
    };
    const response = await this.fpAxios.post<ProspectDto>(`${this.baseUrl}crm/deals`, prospectToSend);
    this.changeStatus(response.data.id!, ProspectStatus.CALLED);
    const prospectResponse = new Prospect(response.data);
    if (prospect.activity) {
      prospectResponse.activity = new Activity(prospect.activity);
    }

    return prospectResponse;
  }

  async getProspectHistoryByPage(phone: string, page: number, rowsPerPage: number = 10):
    Promise<FiltersResponse<HistoryElement<Prospect>>> {
    const requestBody = {
      rowsPerPage: rowsPerPage,
      page: page,
    };
    const response = await this.fpAxios.post(`${this.baseUrl}prospect/history/${phone}`, requestBody);
    return {
      ...response.data,
      items: response.data.items.map((history: HistoryElementDTO<Prospect>) => new HistoryElement(history)),
    };
  }

  async addNote(id: number, data: NoteDTO): Promise<Note> {
    const response = await this.fpAxios.post(`${this.baseUrl}prospect/${id}/notes`, data);
    return new Note(response.data);
  }

  async getProspectLossReasonDictionary(): Promise<ProspectLossReasonDictionaryType[]> {
    const response = await this.fpAxios.get<ProspectLossReasonDictionaryType[]>(`${this.baseUrl}dictionary/loss-reasons`);
    return response.data;
  }

  async getProspectActivity(dealId: number): Promise<Activity> {
    const response = await ActivityApi.getActivities(dealId);
    const activities = response
      .sort((a, b) => (a.startDate! < b.startDate!) ? -1 : ((a.startDate! > b.startDate!) ? 1 : 0));
    return new Activity(activities[0]);
  }

  async getProspectActivities(dealId: number): Promise<Activity[]> {
    return await ActivityApi.getActivities(dealId);
  }

  async generateXLS(options: ProspectFilter) {
    const response = await this.fpAxios.post(`${this.baseUrl}prospects/xls`, options.apiData, {
      responseType: 'blob',
    });
    return new Blob([response.data,], {type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',});
  }

  async searchClientsInOrganisation(phone: string,
    productType: ProductType): Promise<Array<ProspectSearchClientsInOrganisations>> {
    const response = await this.fpAxios.post(`/clients/prospects/search?productType=${productType}`, {
      phone,
    });
    return response.data.map((x: ProspectSearchClientsInOrganisations) => new ProspectSearchClientsInOrganisations(x));
  }

  async getProspectDataFromPhoneNumber(phone: string, productType: ProductType) {
    const response = await this.fpAxios.get(`${this.baseUrl}prospect/phone/${phone}`, {
      params: {
        product_type: productType,
      },
    });
    return response.data;
  }

  async getNotes(prospectId: Number): Promise<ProspectNoteDto[]> {
    const response = await this.fpAxios.get<ProspectNoteDto[]>(`${this.baseUrl}prospect/${prospectId}/notes`);
    return response.data;
  }

  async sortAgents(agents: number[]): Promise<number[]> {
    try {
      if (agents.length === 0) return [];
      const response = await this.fpAxios.post<number[]>('users/sort/cvr', agents);
      return response.data;
    } catch (e) {
      return agents;
    }
  }
}

export default new ProspectApi();
